import { useEffect, useRef } from 'react';
import { useSources } from '../context/SourcesContext';
import { useDialog } from '../context/DialogContext';
import { shouldPromptForClientData } from '../utils';
import { isEmbedded } from '../lib/embedMode';

// COM-636: renders in every mode (standalone and embedded), but the
// auto-open itself only fires in standalone mode — see the `isEmbedded()`
// guard below.
//
// Product decision (2026-08-14): inside the ComplyControl iframe, an
// unconfigured project must never get this dialog popped over it. CC
// configures the project through its own embedded Settings page (COM-638),
// not by a popup stealing focus over whichever embedded screen — articles
// or settings — happens to be showing. Before this guard the dialog opened
// over both embedded views, which is the bug this fix closes; standalone
// mode is unaffected and still gets the prompt.
//
// This consolidates what used to be two separate, overlapping auto-open
// effects:
//   - Header.tsx used to open the dialog when `shouldAskClientData` was
//     true. That flag (SourcesContext.tsx) computes
//     `Object.entries(currentProject).every(...)` over the FULL project
//     object (id, name, role, createdAt, updatedAt, gptModel, ...profile
//     fields), requiring every entry — including non-profile keys like
//     `id` — to be both a known profile key AND falsy. A real project
//     always carries `id`/`name`/etc, so this can never be true in
//     production; it was dead code.
//   - UserProfile.tsx (rendered inside Header, so it also disappeared in
//     embedded mode) opened the dialog when `selectedProject.companyType`
//     was a present-but-empty array. This is the condition that actually
//     fired.
// `shouldPromptForClientData` keeps only the working condition.
const OnboardingGate: React.FC = () => {
  const { isConnected, selectedProject, canManageProject } = useSources();
  const { openClientDataDialog } = useDialog();

  // COM-637 follow-up: auto-open at most once per project per mount. A
  // standalone admin can now close the wizard (`canDismissOnboarding`), and
  // without this the next re-render that changes `selectedProject`'s identity
  // - a reconnect, a project refresh - would reopen what they just dismissed.
  // It also means a non-admin who somehow closes it does not get it back
  // without a reload, which is the same contract a reload already had.
  const promptedProjectIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!isConnected || isEmbedded()) {
      return;
    }

    // BLOCKER FIX (COM-637 whole-branch review): the wizard is full-screen
    // and non-closable, and every field/the Finish button is disabled for
    // anyone who cannot manage the project (ProjectSettingsFields.tsx,
    // ClientDataDialog.tsx). Opening it purely off `shouldPromptForClientData`
    // - with no role check - permanently locks a non-manager out of the app
    // on any unconfigured project: they can never satisfy validation, never
    // reach Finish, and the selected project persists server-side so a
    // reload doesn't help either. Reachable via `addUserToProject` (always
    // creates VIEWERs) and COM-636's embed handshake (creates projects with
    // empty client data). Only auto-open for someone who can actually
    // complete it.
    if (!canManageProject()) {
      return;
    }

    const projectId = selectedProject?.id;
    if (!projectId || promptedProjectIdsRef.current.has(projectId)) {
      return;
    }

    if (shouldPromptForClientData(selectedProject?.companyType)) {
      promptedProjectIdsRef.current.add(projectId);
      openClientDataDialog();
    }
  }, [isConnected, selectedProject, openClientDataDialog, canManageProject]);

  return null;
};

export default OnboardingGate;
